import CloseIcon from "@/icons/CloseIcon";
import {
  capitalizeFirstLetter,
  formatCamelCase,
  formatDate,
  formatISODate,
} from "@/lib/utils";
import { useQueryParams } from "@/utils/useQueryParams";
import { useMemo } from "react";

const FilterTag = ({ label, onRemove, type = "" }) => {
  return (
    <div className="border border-gray-400 px-3 py-1 rounded-full flex items-center space-x-2">
      <span className="truncate text-xs">
        <span className="font-semibold">{type}</span> {label}
      </span>
      <button
        onClick={onRemove}
        className="text-xs  text-gray-600 hover:text-gray-500"
      >
        <CloseIcon width="12" height="12" fill="gray" />
      </button>
    </div>
  );
};

const AppliedFilter = ({ filterDetails }) => {
  const {
    type,
    onDisplay,
    isAvailabilty,
    deliveryDate,
    brandIds,
    fieldValueIds,
    fieldIds,
    setQueryParams,
    categoryIds,
    onSale,
    clearance,
    rebateAvailable,
    validUntil,
    isExpired,
    height,
    width,
    depth,
    totalCapacity,
    iceCapacity,
    dailyIceProduction,
    ovenCapacity,
    washerHeight,
    washerWidth,
    washerDepth,
    dryerHeight,
    dryerWidth,
    dryerDepth,
    capacity,
    setProductCategoryIds
  } = useQueryParams();

  const appliedFilters = useMemo(
    () => ({
      brands: brandIds ? brandIds.split(",") : [],
      dynamicFilters: fieldValueIds ? fieldValueIds.split(",") : [],
      fieldIds: fieldIds ? fieldIds.split(",") : [],
      types: type ? type.split(",") : [],
      sale: onSale === "true",
      clearance: clearance === "true",
      rebate: rebateAvailable === "true",
      availability: isAvailabilty === "true",
      deliveryDate,
      validUntil,
      isExpired: isExpired === "true",
      onDisplay: onDisplay === "true",
      rangeValue: {
        height: height ? height.split(",") : [],
        width: width ? width.split(",") : [],
        depth: depth ? depth.split(",") : [],
        totalCapacity: totalCapacity ? totalCapacity.split(",") : [],
        iceCapacity: iceCapacity ? iceCapacity.split(",") : [],
        dailyIceProduction: dailyIceProduction
          ? dailyIceProduction.split(",")
          : [],
        ovenCapacity: ovenCapacity ? ovenCapacity.split(",") : [],
        washerHeight: washerHeight ? washerHeight.split(",") : [],
        washerWidth: washerWidth ? washerWidth.split(",") : [],
        washerDepth: washerDepth ? washerDepth.split(",") : [],
        dryerHeight: dryerHeight ? dryerHeight.split(",") : [],
        dryerWidth: dryerWidth ? dryerWidth.split(",") : [],
        dryerDepth: dryerDepth ? dryerDepth.split(",") : [],
        capacity: capacity ? capacity.split(",") : [],
      },
      setProductCategoryIds: setProductCategoryIds ? setProductCategoryIds.split(",") : [],
    }),
    [
      brandIds,
      categoryIds,
      fieldValueIds,
      fieldIds,
      type,
      onSale,
      clearance,
      rebateAvailable,
      isAvailabilty,
      deliveryDate,
      validUntil,
      isExpired,
      onDisplay,
      height,
      width,
      depth,
      totalCapacity,
      iceCapacity,
      dailyIceProduction,
      ovenCapacity,
      washerHeight,
      washerWidth,
      washerDepth,
      dryerHeight,
      dryerWidth,
      dryerDepth,
      capacity,
      setProductCategoryIds
    ]
  );

  const {
    data: filtersList = [],
    matchedBrandIds,
    brands,
    appliances = [],
  } = filterDetails || {};

  const removeFilter = (filterType, value) => {
    switch (filterType) {
      case "brand":
        setQueryParams({
          brandIds: appliedFilters?.brands
            .filter((id) => id !== value)
            .toString(),
          ...(value &&
            matchedBrandIds?.includes(Number(value)) && { keyword: "" }),
        });
        break;
      case "dynamic":
        setQueryParams({
          fieldValueIds: appliedFilters?.dynamicFilters
            .filter((id) => id !== value)
            .toString(),
        });
        break;
      case "type":
        setQueryParams({
          type: appliedFilters?.types.filter((id) => id !== value).toString(),
        });
        break;
      case "sale":
        setQueryParams({ onSale: "" });
        break;
      case "clearance":
        setQueryParams({ clearance: "" });
        break;
      case "rebate":
        setQueryParams({ rebateAvailable: "" });
        break;
      case "availability":
        setQueryParams({ isAvailabilty: "" });
        break;
      case "deliveryDate":
        setQueryParams({ deliveryDate: "", zipCode: "", deliveryRouteId: "" });
        break;
      case "onDisplay":
        setQueryParams({ onDisplay: "", storeId: "" });
        break;
      case "validUntil":
        setQueryParams({ validUntil: "" });
        break;
      case "isExpired":
        setQueryParams({ isExpired: "false" });
        break;
      case "rangeFilter":
        setQueryParams({
          [value.key]: appliedFilters?.rangeValue[value.key]
            .filter((range) => range !== value.range)
            .join(","),
        });
        break;
      case "Appliances":
        setQueryParams({
          setProductCategoryIds: appliedFilters?.setProductCategoryIds
            .filter((id) => id !== value)
            .toString(),
        });
        break;
      default:
        break;
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {/* Brand Filters */}
      {appliedFilters?.brands.map((brandId) => {
        const brand = brands?.find(
          (b) => String(b.brandId) === String(brandId)
        );
        return brand ? (
          <FilterTag
            key={`brand-${brandId}`}
            label={capitalizeFirstLetter(brand.brandName)}
            onRemove={() => removeFilter("brand", brandId)}
            type="Brand"
          />
        ) : null;
      })}

      {/* Appliances Filters */}
      {appliedFilters?.setProductCategoryIds.map((categoryId) => {
        const category = appliances?.find(
          (c) => String(c.categoryId) === String(categoryId)
        );
        return category ? (
          <FilterTag
            key={`Appliances-${categoryId}`}
            label={capitalizeFirstLetter(category.categoryName)}
            onRemove={() => removeFilter("Appliances", categoryId)}
            type="Appliances"
          />
        ) : null;
      })}

      {/* TYPE Filters */}
      {appliedFilters?.types.map((typeName) => {
        const getTypeLabel = (type) => {
          if (type === "exclusive") {
            return "Airport Home Appliance Exclusive";
          }
          return capitalizeFirstLetter(type);
        };
        return (
          <FilterTag
            key={`type-${typeName}`}
            label={getTypeLabel(typeName)}
            onRemove={() => removeFilter("type", typeName)}
            type="Type"
          />
        );
      })}

      {/* Dynamic Field Filters */}
      {appliedFilters?.dynamicFilters.map((fieldId) => {
        const field = filtersList
          ?.flatMap((f) => f.fields || [])
          .flatMap((f) => f.fieldValues || [])
          .find((f) => String(f.fieldValueId) === String(fieldId));

        if (!field) return null;

        const parentField = filtersList
          ?.flatMap((f) => f.fields || [])
          .find((f) =>
            f.fieldValues.some(
              (v) => String(v.fieldValueId) === String(fieldId)
            )
          );

        return (
          <FilterTag
            key={`field-${fieldId}`}
            label={field.fieldValue}
            onRemove={() => removeFilter("dynamic", fieldId)}
            type={parentField?.fieldName || "Field"}
          />
        );
      })}

      {/* Other Filters */}
      {appliedFilters?.sale && (
        <FilterTag onRemove={() => removeFilter("sale")} type="On Sale" />
      )}
      {appliedFilters?.clearance && (
        <FilterTag
          onRemove={() => removeFilter("clearance")}
          type="Clearance"
        />
      )}
      {appliedFilters?.rebate && (
        <FilterTag
          onRemove={() => removeFilter("rebate")}
          type="Rebate Available"
        />
      )}
      {appliedFilters?.availability && (
        <FilterTag
          onRemove={() => removeFilter("availability")}
          type="In Stock"
        />
      )}
      {appliedFilters?.deliveryDate && (
        <FilterTag
          label={formatISODate(appliedFilters?.deliveryDate, "EEE, MMM dd")}
          onRemove={() => removeFilter("deliveryDate")}
          type="Delivery"
        />
      )}
      {appliedFilters?.onDisplay && (
        <FilterTag
          onRemove={() => removeFilter("onDisplay")}
          type="On Display at Store Today"
        />
      )}
      {appliedFilters?.validUntil && (
        <FilterTag
          label={`Valid Until: ${formatDate(
            appliedFilters?.validUntil,
            "MMM dd, yyyy"
          )}`}
          onRemove={() => removeFilter("validUntil")}
          type="Validity"
        />
      )}
      {appliedFilters?.isExpired && (
        <FilterTag
          label="Recently Expired Rebates"
          onRemove={() => removeFilter("isExpired")}
          type="Validity"
        />
      )}

      {Object.entries(appliedFilters?.rangeValue).map(([key, values]) =>
        values.map((range) => (
          <FilterTag
            key={`${key}-${range}`}
            label={`${range.replace("-", '" - ')}"`}
            onRemove={() => removeFilter("rangeFilter", { key, range })}
            type={formatCamelCase(key)}
          />
        ))
      )}
    </div>
  );
};

export default AppliedFilter;
